import React, { useEffect, useState, useMemo } from 'react';
import { BarChart2, Search, X } from 'lucide-react';
import api from '../../services/api';

const fmt = d => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

const TYPE_META = {
  b2b:         { label: 'B2B – School Batch', color: '#1D4ED8', bg: '#EEF2FF', border: '#BFDBFE' },
  direct_sale: { label: 'Direct Sale',        color: '#C84B0F', bg: '#FEF3EE', border: '#FDCFB5' },
  legacy:      { label: 'Legacy',             color: '#6B7280', bg: '#F9FAFB', border: '#E5E7EB' },
};

const statusOf = c => c.stdOne && c.stdTwo ? 'both' : c.stdOne ? 'login1' : 'none';

export default function RedemptionPage() {
  const [codes, setCodes]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const PAGE = 50;
  const [page, setPage]     = useState(1);

  useEffect(() => {
    api.get('/school-portal/redemption')
      .then(r => setCodes(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return codes.filter(c => {
      if (q && !c.code?.toLowerCase().includes(q)
            && !c.stdOne_mobile?.includes(q)
            && !c.stdTwo_mobile?.includes(q)) return false;
      if (typeFilter && c.code_type !== typeFilter) return false;
      if (statusFilter === 'both'   && statusOf(c) !== 'both')   return false;
      if (statusFilter === 'login1' && statusOf(c) !== 'login1') return false;
      if (statusFilter === 'none'   && statusOf(c) !== 'none')   return false;
      return true;
    });
  }, [codes, search, typeFilter, statusFilter]);

  const totalPages = Math.ceil(filtered.length / PAGE);
  const pageRows   = filtered.slice((page - 1) * PAGE, page * PAGE);

  const totals = useMemo(() => ({
    total:  codes.length,
    login1: codes.filter(c => c.stdOne).length,
    login2: codes.filter(c => c.stdTwo).length,
    none:   codes.filter(c => !c.stdOne && !c.stdTwo).length,
  }), [codes]);

  const handleFilter = (setter, val) => { setter(val); setPage(1); };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <BarChart2 size={22} color="#C84B0F" />
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1C0A00', margin: 0 }}>Redemption Report</h1>
      </div>
      <p style={{ fontSize: 13, color: '#9CA3AF', marginBottom: 22 }}>All codes issued to your school — redeemed and pending</p>

      {/* Metric cards */}
      {!loading && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 22 }}>
          {[
            { label: 'Total Codes',  value: totals.total,  color: '#C84B0F', bg: '#FEF3EE', border: '#FDCFB5' },
            { label: 'Login 1 Done', value: totals.login1, color: '#15803D', bg: '#F0FDF4', border: '#BBF7D0' },
            { label: 'Login 2 Done', value: totals.login2, color: '#0F766E', bg: '#F0FDFA', border: '#99F6E4' },
            { label: 'Not Redeemed',  value: totals.none,  color: '#B91C1C', bg: '#FEF2F2', border: '#FECACA' },
          ].map(m => (
            <div key={m.label} style={{ background: m.bg, border: `1px solid ${m.border}`, borderRadius: 12, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: m.color, textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.8, marginBottom: 6 }}>{m.label}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: m.color, fontVariantNumeric: 'tabular-nums' }}>{m.value.toLocaleString('en-IN')}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="card" style={{ padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 8, background: '#FFF8F4', border: '1px solid #F0DCC8', borderRadius: 8, padding: '7px 11px' }}>
          <Search size={13} color="#C84B0F" />
          <input placeholder="Search code or mobile…" value={search} onChange={e => handleFilter(setSearch, e.target.value)}
            style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, color: '#1C0A00', flex: 1 }} />
          {search && <button onClick={() => handleFilter(setSearch, '')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 0, display: 'flex' }}><X size={12} /></button>}
        </div>
        <select value={typeFilter} onChange={e => handleFilter(setTypeFilter, e.target.value)}
          style={{ padding: '7px 28px 7px 10px', border: '1px solid #F0DCC8', borderRadius: 8, fontSize: 13, color: '#1C0A00', background: '#FFFCFA', appearance: 'none', outline: 'none', cursor: 'pointer' }}>
          <option value="">All types</option>
          <option value="b2b">B2B – School Batch</option>
          <option value="direct_sale">Direct Sale</option>
          <option value="legacy">Legacy</option>
        </select>
        <select value={statusFilter} onChange={e => handleFilter(setStatusFilter, e.target.value)}
          style={{ padding: '7px 28px 7px 10px', border: '1px solid #F0DCC8', borderRadius: 8, fontSize: 13, color: '#1C0A00', background: '#FFFCFA', appearance: 'none', outline: 'none', cursor: 'pointer' }}>
          <option value="">All status</option>
          <option value="both">Both logins done</option>
          <option value="login1">Login 1 only</option>
          <option value="none">Not redeemed</option>
        </select>
        <span style={{ fontSize: 12, color: '#9CA3AF', whiteSpace: 'nowrap' }}>{filtered.length} of {codes.length} codes</span>
      </div>

      {/* Table */}
      <div className="card" style={{ overflow: 'hidden' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48, gap: 12, color: '#9CA3AF' }}>
            <div className="loader" /> Loading…
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    {['Code', 'Type', 'Order Ref', 'Batch', 'Login 1 (mobile)', 'Login 1 date', 'Login 2 (mobile)', 'Login 2 date', 'Status'].map(h => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.length === 0 ? (
                    <tr><td colSpan={9} style={{ textAlign: 'center', padding: '40px 0', color: '#9CA3AF' }}>No codes found</td></tr>
                  ) : pageRows.map((c, i) => {
                    const meta   = TYPE_META[c.code_type] || TYPE_META.legacy;
                    const status = statusOf(c);
                    const orderRef = c.direct_batch_id ? (c.order_ref || `ORD-${c.direct_batch_id}`) : c.print_code ? `Print: ${c.print_code}` : c.order_id ? `Order #${c.order_id}` : '—';
                    return (
                      <tr key={c.code} style={{ background: i % 2 === 0 ? '#fff' : '#FFFCFA' }}>
                        <td>
                          <code style={{ background: '#FEF3EE', color: '#C84B0F', padding: '2px 7px', borderRadius: 5, fontSize: 12, fontWeight: 700 }}>{c.code}</code>
                        </td>
                        <td>
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: meta.bg, color: meta.color, border: `1px solid ${meta.border}`, whiteSpace: 'nowrap' }}>
                            {meta.label}
                          </span>
                        </td>
                        <td style={{ fontSize: 12, color: '#6B4422', fontFamily: 'monospace', fontWeight: 600 }}>{orderRef}</td>
                        <td style={{ fontSize: 12, color: '#374151' }}>{c.batch_name}</td>
                        <td>
                          {c.stdOne ? (
                            <div>
                              <div style={{ fontWeight: 700, color: '#15803D', fontSize: 13 }}>{c.stdOne_mobile || c.stdOne}</div>
                              {c.stdOne_name && <div style={{ fontSize: 11, color: '#9CA3AF' }}>{c.stdOne_name}</div>}
                            </div>
                          ) : <span style={{ color: '#D1D5DB' }}>—</span>}
                        </td>
                        <td style={{ fontSize: 12, color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                          {fmt(c.stdOne_updated_at) || (c.stdOne ? 'Pre-migration' : '—')}
                        </td>
                        <td>
                          {c.stdTwo ? (
                            <div>
                              <div style={{ fontWeight: 700, color: '#0F766E', fontSize: 13 }}>{c.stdTwo_mobile || c.stdTwo}</div>
                              {c.stdTwo_name && <div style={{ fontSize: 11, color: '#9CA3AF' }}>{c.stdTwo_name}</div>}
                            </div>
                          ) : <span style={{ color: '#D1D5DB' }}>—</span>}
                        </td>
                        <td style={{ fontSize: 12, color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                          {fmt(c.stdTwo_updated_at) || (c.stdTwo ? 'Pre-migration' : '—')}
                        </td>
                        <td>
                          {status === 'both'   && <span className="badge badge-green">Both ✓✓</span>}
                          {status === 'login1' && <span className="badge badge-blue">Login 1 only</span>}
                          {status === 'none'   && <span className="badge badge-grey">Pending</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ padding: '12px 16px', borderTop: '1px solid #FDF0E8', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: '#9CA3AF' }}>Page {page} of {totalPages} · {filtered.length} codes</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    style={{ padding: '5px 10px', border: '1px solid #F0DCC8', borderRadius: 7, background: '#fff', color: page === 1 ? '#D1D5DB' : '#C84B0F', cursor: page === 1 ? 'not-allowed' : 'pointer' }}>←</button>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                    style={{ padding: '5px 10px', border: '1px solid #F0DCC8', borderRadius: 7, background: '#fff', color: page === totalPages ? '#D1D5DB' : '#C84B0F', cursor: page === totalPages ? 'not-allowed' : 'pointer' }}>→</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
